import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormDataShape } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { toTitleCase } from '../../../../common/utils/to-title-case';
import { Checkout } from '../../../../common/types/checkout';
import styled from 'styled-components';
import { MordenCard } from '../../../atoms/modern-card/modern-card';
import InputMask from 'react-input-mask';
import { phoneMasks } from '../../../../common/utils/phone-masks';
import { documentMasks } from '../../../../common/utils/document-masks';

const Fieldset = styled(MordenCard)`
  margin-top: 12px;

  legend {
    font-weight: 700;
    font-size: 22px;
    margin-bottom: 12px;
    color: #18233A;
    letter-spacing: -0.3px;
  }

  .grid {
    display: grid;
    gap: 10px;
  }

  @media (min-width: 600px) {
    .grid-2 {
      grid-template-columns: 1fr 1fr;
    }
    .grid-3 {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @media (max-width: 380px) {
    legend {
      font-size: 18px;
    }
  }
`;

const InputWrap = styled.div`
  position: relative;

  label {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--muted);
    font-size: 14px;
    transition: all 0.15s ease;
    pointer-events: none;
    background: var(--card);
    padding: 0 6px;
  }

  input,
  select {
    width: 100%;
    height: 48px;
    border-radius: 10px;
    border: 1.5px solid #D9DDE3;
    padding: 0 14px;
    background: #fff;
    font-size: 15px;
    color: #18233A;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  input:hover,
  select:hover {
    border-color: #b0b5bd;
  }

  input:focus,
  select:focus {
    outline: 0;
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(40, 121, 254, 0.1);
  }

  input:not(:placeholder-shown) + label,
  input:focus + label,
  select:focus + label {
    top: 0;
    transform: translateY(-50%) scale(0.85);
    color: var(--muted);
    font-size: 13px;
  }

  @media (max-width: 380px) {
    input,
    select {
      height: 44px;
      font-size: 14px;
      padding: 0 12px;
    }
  }
`;

type Props = {
  formData: FormDataShape;
  setFormData: (field: keyof FormDataShape, value: string) => void;
  checkout: Checkout;
};

export const CheckoutFormModern: React.FC<Props> = ({
  formData,
  setFormData,
  checkout,
}) => {
  const { t } = useTranslation();
  return (
    <Fieldset>
      <legend>{t('contact')}</legend>
      <div className="grid grid-1">
        <InputWrap>
          <input
            id="email"
            placeholder=" "
            value={formData.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData('email', e.target.value.toLowerCase())
            }
            type="email"
          />
          <label>{t('email')}*</label>
        </InputWrap>
        <InputWrap>
          <input
            id="fullName"
            placeholder=" "
            value={formData.fullName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData('fullName', toTitleCase(e.target.value))
            }
            type="text"
          />
          <label>{t('name')}*</label>
        </InputWrap>
        <InputWrap>
          <InputMask
            id="phoneNumber"
            mask={phoneMasks[checkout.paisEnum]}
            maskChar=""
            placeholder=" "
            type="tel"
            value={formData.phoneNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData('phoneNumber', e.target.value)
            }
          >
            {(inputProps: any) => <input {...inputProps} />}
          </InputMask>
          <label>{t('cellphone')}*</label>
        </InputWrap>
      </div>
    </Fieldset>
  );
};
